// src/pages/patient/BookAppointment.jsx
//
// /patient/book-appointment -- the canonical standard booking/discovery route.
//
//   1. search and filter services       (this page)
//   2-6. review, mode, location, dates, time  (ServiceBookingPanel)
//   7. continue into hold/book/payment  (HoldAndBook, unchanged behaviour)
//
// Request discipline:
//   * text and select changes settle for 350 ms, then exactly one search runs;
//   * every search is sequenced and aborted when superseded, so a slow older
//     response never replaces a newer one;
//   * results stay on screen while a refresh is in flight;
//   * filter options load separately and never block the results;
//   * the URL mirrors the settled search so it can be shared and survives
//     back-navigation.
//
// The display timezone is sent explicitly on every call. Totals, pages and
// availability counts all come from the server's complete filtered result.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { readApiError } from "../../lib/apiErrors";
import {
  addDaysIso,
  browserTimezone,
  dateRangeProblem,
  defaultDateRange,
  formatDateTimeInZone,
  todayInZone,
  MAX_RANGE_DAYS,
} from "../../lib/displayTimezone";
import useSequencedRequest from "../../lib/useSequencedRequest";
import { patientDiscoveryApi } from "../../services/patientDiscovery";
import ServiceBookingPanel from "../../components/patient/booking/ServiceBookingPanel";
import {
  Icon,
  InlineError,
  Pill,
  RatingBadge,
  TimezoneNotice,
  classNames,
  modeLabel,
  modeTone,
  money,
} from "../../components/patient/booking/bookingUi";

export const SEARCH_DEBOUNCE_MS = 350;
const PAGE_SIZE = 12;

const FILTER_KEYS = [
  "q",
  "location",
  "specialty",
  "language",
  "service_type_id",
  "clinic_id",
  "doctor_id",
  "mode",
  "date_from",
  "date_to",
  "price_min",
  "price_max",
  "doctor_gender",
  "availability_only",
  "page",
  "tz",
];

function filtersFromParams(params, fallbackTimezone) {
  const tz = params.get("tz") || fallbackTimezone;
  const defaults = defaultDateRange(tz);
  const page = Number(params.get("page") || 1);
  return {
    q: params.get("q") || "",
    location: params.get("location") || "",
    specialty: params.get("specialty") || "",
    language: params.get("language") || "",
    service_type_id: params.get("service_type_id") || "",
    clinic_id: params.get("clinic_id") || "",
    doctor_id: params.get("doctor_id") || "",
    mode: params.get("mode") || "",
    date_from: params.get("date_from") || defaults.dateFrom,
    date_to: params.get("date_to") || defaults.dateTo,
    price_min: params.get("price_min") || "",
    price_max: params.get("price_max") || "",
    doctor_gender: ["female", "male"].includes(params.get("doctor_gender")) ? params.get("doctor_gender") : "",
    availability_only: params.get("availability_only") === "true",
    page: Number.isFinite(page) && page > 0 ? page : 1,
    tz,
  };
}

function emptyFilters(tz) {
  const defaults = defaultDateRange(tz);
  return {
    q: "",
    location: "",
    specialty: "",
    language: "",
    service_type_id: "",
    clinic_id: "",
    doctor_id: "",
    mode: "",
    date_from: defaults.dateFrom,
    date_to: defaults.dateTo,
    price_min: "",
    price_max: "",
    doctor_gender: "",
    availability_only: false,
    page: 1,
    tz,
  };
}

function useDebouncedValue(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(id);
  }, [value, delay]);
  return debounced;
}

function priceProblem(min, max) {
  const low = min === "" ? null : Number(min);
  const high = max === "" ? null : Number(max);
  if ((low !== null && (!Number.isFinite(low) || low < 0)) || (high !== null && (!Number.isFinite(high) || high < 0))) {
    return "Prices must be zero or more.";
  }
  if (low !== null && high !== null && low > high) return "Minimum price cannot be more than the maximum price.";
  return "";
}

function FieldLabel({ htmlFor, children }) {
  return (
    <label htmlFor={htmlFor} className="text-xs font-semibold uppercase tracking-wide text-muted">
      {children}
    </label>
  );
}

function SelectField({ id, value, onChange, options, placeholder }) {
  return (
    <select
      id={id}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="mt-2 w-full rounded-2xl border border-border bg-white px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-primary/30"
    >
      <option value="">{placeholder}</option>
      {(options || []).map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
}

function ServiceCard({ item, displayTimezone, onChoose }) {
  const count = Number(item.availability_count || 0);
  const blocked = item.eligibility?.can_proceed === false;
  const doctorName = item.doctor_display_name || item.doctor_full_name;

  return (
    <article className="flex flex-col overflow-hidden rounded-3xl border border-border bg-white shadow-soft" aria-label={item.service_name} data-testid="service-card">
      <div className="relative h-40 w-full bg-surface">
        {item.cover_image_url ? (
          <img src={item.cover_image_url} alt={item.cover_image_alt_text || ""} className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full items-center justify-center text-slate-400">
            <Icon name={item.supported_modes?.includes("in_clinic") ? "map" : "video"} className="h-8 w-8" />
          </div>
        )}
        <div className="absolute left-4 top-4 flex flex-wrap gap-2">
          {(item.supported_modes || []).map((mode) => (
            <Pill key={mode} tone={modeTone(mode)}>{modeLabel(mode)}</Pill>
          ))}
        </div>
      </div>

      <div className="flex flex-1 flex-col p-5">
        <div className="flex flex-wrap items-center gap-2">
          <Pill tone="info">{item.service_type_name}</Pill>
          {count > 0 ? <Pill tone="success">{count} time{count === 1 ? "" : "s"} in these dates</Pill> : <Pill tone="warn">No times in these dates</Pill>}
          <RatingBadge rating={item.service_avg_rating} count={item.service_review_count} />
        </div>

        <h3 className="mt-3 text-lg font-semibold">{item.service_name}</h3>
        <div className="mt-1 text-sm text-muted">
          {doctorName}
          {item.primary_specialization ? ` • ${item.primary_specialization}` : ""}
        </div>
        <div className="mt-1 text-sm text-muted">
          {item.clinic_name}
          {item.bookable_locations?.length ? ` • ${[...new Set(item.bookable_locations.map((l) => l.city).filter(Boolean))].join(", ")}` : ""}
        </div>
        <p className="mt-3 line-clamp-2 text-sm text-muted">{item.short_description || item.full_description || "No description available."}</p>

        <div className="mt-4 flex flex-wrap gap-2">
          <Pill tone="neutral">{money(item.price, item.currency)}</Pill>
          <Pill tone="neutral">{item.duration_min} min</Pill>
          {item.before_forms_count > 0 ? <Pill tone="violet">{item.before_forms_count} pre-visit form(s)</Pill> : null}
          {item.eligibility?.age_limit_text ? <Pill tone="warn">{item.eligibility.age_limit_text}</Pill> : null}
        </div>

        {blocked ? (
          <p className="mt-3 rounded-2xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900" data-testid="card-eligibility">
            {item.eligibility.message}
          </p>
        ) : null}

        <div className="mt-auto flex items-center justify-between gap-3 pt-4">
          <div className="min-w-0 text-xs text-muted">
            Next: {item.next_available_at ? formatDateTimeInZone(item.next_available_at, displayTimezone) : "—"}
          </div>
          <button
            type="button"
            onClick={() => onChoose(item)}
            className="inline-flex shrink-0 items-center gap-2 rounded-2xl border border-border bg-white px-4 py-2.5 text-sm font-semibold hover:bg-slate-50"
            aria-label={`Choose ${item.service_name}`}
          >
            {blocked ? "View details" : "Choose"}
          </button>
        </div>
      </div>
    </article>
  );
}

// Filters that differ from their defaults (dates count once, as a range).
export function activeFilterCount(filters) {
  const defaults = defaultDateRange(filters.tz);
  let count = 0;
  ["q", "location", "specialty", "language", "service_type_id", "clinic_id", "doctor_id", "mode", "doctor_gender"].forEach((key) => {
    if (String(filters[key] || "").trim()) count += 1;
  });
  if (filters.price_min !== "" || filters.price_max !== "") count += 1;
  if (filters.availability_only) count += 1;
  if (filters.date_from !== defaults.dateFrom || filters.date_to !== defaults.dateTo) count += 1;
  return count;
}

function DiscoveryFilters({ idPrefix, filters, opt, update, changeTimezone, clearFilters, timezoneChoices, deviceTimezone, liveProblem, optionsError, retryOptions, activeCount }) {
  const id = (name) => `${idPrefix}-${name}`;
  const input = "mt-2 w-full rounded-2xl border border-border bg-white px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-primary/30";
  const today = todayInZone(filters.tz);
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-base font-semibold">Filters</h2>
        <button type="button" onClick={clearFilters} disabled={!activeCount} className="rounded-xl px-2 py-1 text-xs font-semibold text-primary hover:underline disabled:cursor-not-allowed disabled:text-muted disabled:no-underline">
          Clear filters
        </button>
      </div>

      <div>
        <FieldLabel htmlFor={id("q")}>Search</FieldLabel>
        <div className="relative">
          <span className="pointer-events-none absolute left-3 top-1/2 mt-1 -translate-y-1/2 text-muted">
            <Icon name="search" className="h-4 w-4" />
          </span>
          <input
            id={id("q")}
            type="search"
            maxLength={100}
            className={classNames(input, "pl-10")}
            value={filters.q}
            onChange={(e) => update("q", e.target.value)}
            placeholder="Service, doctor, clinic or speciality"
            aria-describedby={id("q-help")}
          />
        </div>
        <span id={id("q-help")} className="sr-only">Results update as you type.</span>
      </div>

      <div>
        <FieldLabel htmlFor={id("mode")}>Consultation type</FieldLabel>
        <SelectField id={id("mode")} value={filters.mode} placeholder="Any type" options={(opt.modes || []).map((m) => ({ value: m.value, label: m.label }))} onChange={(value) => update("mode", value)} />
      </div>
      <div>
        <FieldLabel htmlFor={id("type")}>Service type</FieldLabel>
        <SelectField id={id("type")} value={filters.service_type_id} placeholder="All service types" options={opt.service_types} onChange={(value) => update("service_type_id", value)} />
      </div>
      <div>
        <FieldLabel htmlFor={id("location")}>City</FieldLabel>
        <SelectField id={id("location")} value={filters.location} placeholder="All cities" options={opt.locations} onChange={(value) => update("location", value)} />
      </div>
      <div>
        <FieldLabel htmlFor={id("specialty")}>Speciality</FieldLabel>
        <SelectField id={id("specialty")} value={filters.specialty} placeholder="All specialities" options={opt.specialties} onChange={(value) => update("specialty", value)} />
      </div>
      <div>
        <FieldLabel htmlFor={id("clinic")}>Clinic</FieldLabel>
        <SelectField id={id("clinic")} value={filters.clinic_id} placeholder="All clinics" options={opt.clinics} onChange={(value) => update("clinic_id", value)} />
      </div>
      <div>
        <FieldLabel htmlFor={id("doctor")}>Doctor</FieldLabel>
        <SelectField id={id("doctor")} value={filters.doctor_id} placeholder="All doctors" options={opt.doctors} onChange={(value) => update("doctor_id", value)} />
      </div>
      <div>
        <FieldLabel htmlFor={id("gender")}>Provider gender</FieldLabel>
        <SelectField id={id("gender")} value={filters.doctor_gender} placeholder="Any provider" options={opt.genders} onChange={(value) => update("doctor_gender", value)} />
      </div>
      <div>
        <FieldLabel htmlFor={id("language")}>Language</FieldLabel>
        <SelectField id={id("language")} value={filters.language} placeholder="Any language" options={opt.languages} onChange={(value) => update("language", value)} />
      </div>

      <fieldset>
        <legend className="text-xs font-semibold uppercase tracking-wide text-muted">Dates</legend>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label htmlFor={id("from")} className="sr-only">From</label>
            <input id={id("from")} aria-label="From" className={input} type="date" min={today} value={filters.date_from} onChange={(e) => update("date_from", e.target.value)} />
          </div>
          <div>
            <label htmlFor={id("to")} className="sr-only">To</label>
            <input id={id("to")} aria-label="To" className={input} type="date" min={filters.date_from || today} max={filters.date_from ? addDaysIso(filters.date_from, MAX_RANGE_DAYS - 1) : undefined} value={filters.date_to} onChange={(e) => update("date_to", e.target.value)} />
          </div>
        </div>
      </fieldset>

      <fieldset>
        <legend className="text-xs font-semibold uppercase tracking-wide text-muted">
          Price{opt.currencies?.length === 1 ? ` (${opt.currencies[0]})` : opt.currencies?.length > 1 ? " (each service's currency)" : ""}
        </legend>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label htmlFor={id("price-min")} className="sr-only">Min price</label>
            <input id={id("price-min")} aria-label="Min price" placeholder="Min" className={input} type="number" min="0" inputMode="decimal" value={filters.price_min} onChange={(e) => update("price_min", e.target.value)} />
          </div>
          <div>
            <label htmlFor={id("price-max")} className="sr-only">Max price</label>
            <input id={id("price-max")} aria-label="Max price" placeholder="Max" className={input} type="number" min="0" inputMode="decimal" value={filters.price_max} onChange={(e) => update("price_max", e.target.value)} />
          </div>
        </div>
      </fieldset>

      <div>
        <FieldLabel htmlFor={id("timezone")}>Time zone</FieldLabel>
        <select id={id("timezone")} className={input} value={filters.tz} onChange={(e) => changeTimezone(e.target.value)}>
          {timezoneChoices.map((tz) => (
            <option key={tz} value={tz}>
              {tz === deviceTimezone ? `${tz} (this device)` : tz}
            </option>
          ))}
        </select>
      </div>

      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={filters.availability_only} onChange={(e) => update("availability_only", e.target.checked)} />
        With times only
      </label>

      {liveProblem ? <p role="alert" className="text-sm font-semibold text-rose-700">{liveProblem}</p> : null}
      {optionsError ? <InlineError message="Filter choices could not be loaded. You can still search by text and dates." onRetry={retryOptions} /> : null}
    </div>
  );
}

const DRAWER_FOCUSABLE = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

function FilterDrawer({ onClose, activeCount, total, children }) {
  const ref = useRef(null);
  useEffect(() => {
    ref.current?.querySelector(DRAWER_FOCUSABLE)?.focus();
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key !== "Tab") return;
      const nodes = Array.from(ref.current?.querySelectorAll(DRAWER_FOCUSABLE) || []);
      if (!nodes.length) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex bg-slate-900/50 lg:hidden" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div ref={ref} role="dialog" aria-modal="true" aria-label="Filters" className="flex h-full w-full max-w-sm flex-col bg-white shadow-2xl" data-testid="filter-drawer">
        <div className="flex items-center justify-between border-b border-border p-4">
          <span className="text-base font-semibold">Filters{activeCount ? ` (${activeCount})` : ""}</span>
          <button type="button" onClick={onClose} className="rounded-2xl border border-border px-3 py-1.5 text-sm font-semibold">Close</button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">{children}</div>
        <div className="border-t border-border p-4">
          <button type="button" onClick={onClose} className="btn-primary-gradient w-full rounded-2xl px-5 py-3 text-sm font-semibold text-white">
            {total === null || total === undefined ? "Show results" : `Show ${total} result${total === 1 ? "" : "s"}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// Compact numbered pagination: first, last and a window around the current page.
export function pageWindow(page, totalPages, radius = 1) {
  const pages = new Set([1, totalPages]);
  for (let p = page - radius; p <= page + radius; p += 1) if (p >= 1 && p <= totalPages) pages.add(p);
  const sorted = Array.from(pages).sort((a, b) => a - b);
  const out = [];
  sorted.forEach((p, i) => {
    if (i && p - sorted[i - 1] > 1) out.push(`gap-${p}`);
    out.push(p);
  });
  return out;
}

function Pagination({ page, totalPages, disabled, onChange }) {
  const button = "min-w-[2.5rem] rounded-2xl border px-3 py-2 text-sm font-semibold disabled:opacity-50";
  return (
    <nav className="mt-8 flex flex-wrap items-center justify-center gap-2" aria-label="Results pages">
      <button type="button" onClick={() => onChange(Math.max(1, page - 1))} disabled={page <= 1 || disabled} className={classNames(button, "border-border bg-white")}>
        Previous
      </button>
      {pageWindow(page, totalPages).map((entry) =>
        typeof entry === "string" ? (
          <span key={entry} className="px-1 text-sm text-muted" aria-hidden="true">…</span>
        ) : (
          <button
            key={entry}
            type="button"
            onClick={() => onChange(entry)}
            disabled={disabled && entry !== page}
            aria-current={entry === page ? "page" : undefined}
            aria-label={`Page ${entry}`}
            className={classNames(button, entry === page ? "border-primary bg-primary text-white" : "border-border bg-white hover:bg-slate-50")}
          >
            {entry}
          </button>
        )
      )}
      <span className="sr-only">Page {page} of {totalPages}</span>
      <button type="button" onClick={() => onChange(Math.min(totalPages, page + 1))} disabled={page >= totalPages || disabled} className={classNames(button, "border-border bg-white")}>
        Next
      </button>
    </nav>
  );
}

export default function BookAppointment() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const deviceTimezone = useMemo(() => browserTimezone(), []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initial = useMemo(() => filtersFromParams(searchParams, deviceTimezone), []);

  const [filters, setFilters] = useState(initial);
  const settled = useDebouncedValue(filters, SEARCH_DEBOUNCE_MS);
  const [retryTick, setRetryTick] = useState(0);
  const [selectedService, setSelectedService] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const drawerOpenerRef = useRef(null);
  const closeDrawer = useCallback(() => {
    setDrawerOpen(false);
    // Focus goes back to the control that opened the drawer.
    window.setTimeout(() => drawerOpenerRef.current?.focus(), 0);
  }, []);
  // Holds the results object that was on screen when the patient recorded a
  // date of birth. The open panel is refreshed only once a NEWER result set has
  // arrived, so it never re-reads the stale eligibility it already had.
  const [resyncAfter, setResyncAfter] = useState(undefined);

  const results = useSequencedRequest(null);
  const options = useSequencedRequest(null);
  const { run: runResults } = results;
  const { run: runOptions } = options;

  const displayTimezone = settled.tz;
  const settledProblem = dateRangeProblem(settled.date_from, settled.date_to) || priceProblem(settled.price_min, settled.price_max);
  const liveProblem = dateRangeProblem(filters.date_from, filters.date_to) || priceProblem(filters.price_min, filters.price_max);

  const settledKey = useMemo(() => JSON.stringify(settled), [settled]);

  useEffect(() => {
    const next = {};
    FILTER_KEYS.forEach((key) => {
      const value = settled[key];
      if (key === "page" && value === 1) return;
      if (key === "availability_only" && !value) return;
      if (value !== "" && value !== null && value !== undefined) next[key] = String(value);
    });
    setSearchParams(next, { replace: true });
    if (settledProblem) return;
    runResults(({ signal }) =>
      patientDiscoveryApi.services(
        {
          q: settled.q,
          location: settled.location,
          specialty: settled.specialty,
          language: settled.language,
          service_type_id: settled.service_type_id,
          clinic_id: settled.clinic_id,
          doctor_id: settled.doctor_id,
          mode: settled.mode,
          date_from: settled.date_from,
          date_to: settled.date_to,
          price_min: settled.price_min,
          price_max: settled.price_max,
          doctor_gender: settled.doctor_gender,
          availability_only: settled.availability_only,
          display_timezone: settled.tz,
          page: settled.page,
          page_size: PAGE_SIZE,
        },
        { signal }
      )
    );
    // settledKey captures every settled value; retryTick re-runs the same search.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settledKey, retryTick]);

  const loadOptions = useCallback(() => {
    runOptions(({ signal }) => patientDiscoveryApi.filterOptions({ mode: settled.mode }, { signal }));
  }, [runOptions, settled.mode]);

  useEffect(() => {
    loadOptions();
  }, [loadOptions]);

  function update(key, value) {
    setFilters((previous) => ({ ...previous, [key]: value, page: 1 }));
  }

  function changeTimezone(tz) {
    setFilters((previous) => {
      // Dates the patient has not touched follow the new zone's "today".
      const previousDefaults = defaultDateRange(previous.tz);
      const untouched = previous.date_from === previousDefaults.dateFrom && previous.date_to === previousDefaults.dateTo;
      const nextDefaults = defaultDateRange(tz);
      return {
        ...previous,
        tz,
        page: 1,
        ...(untouched ? { date_from: nextDefaults.dateFrom, date_to: nextDefaults.dateTo } : {}),
      };
    });
    setSelectedService(null);
  }

  function clearFilters() {
    setFilters(emptyFilters(filters.tz));
  }

  function goToPage(page) {
    setFilters((previous) => ({ ...previous, page }));
  }

  // The panel never decides eligibility itself: it shows whatever the refreshed
  // search says about this service, which may still be "not eligible".
  useEffect(() => {
    if (resyncAfter === undefined || !selectedService) return;
    if (!results.data || results.data === resyncAfter) return; // still the old answer
    const refreshed = results.data.items?.find(
      (item) => item.service_version_id === selectedService.service_version_id
    );
    if (refreshed) setSelectedService(refreshed);
    setResyncAfter(undefined);
  }, [results.data, resyncAfter, selectedService]);

  // Results shrank under the current page (e.g. services were withdrawn):
  // move to the last page that exists instead of showing an empty page.
  useEffect(() => {
    const data = results.data;
    if (!data || results.loading) return;
    const pages = Number(data.total_pages || 0);
    if (data.total > 0 && !(data.items || []).length && settled.page > pages && pages > 0) {
      setFilters((previous) => ({ ...previous, page: pages }));
    }
  }, [results.data, results.loading, settled.page]);

  const opt = options.data || {};
  const items = results.data?.items || [];
  const total = results.data?.total || 0;
  const totalPages = results.data?.total_pages || 0;
  const currentPage = results.data?.page || settled.page;
  const pageSize = results.data?.page_size || PAGE_SIZE;
  const firstShown = total ? (currentPage - 1) * pageSize + 1 : 0;
  const lastShown = total ? Math.min(total, firstShown + items.length - 1) : 0;
  const timezoneChoices = [...new Set([deviceTimezone, filters.tz, "UTC"].filter(Boolean))];
  const activeCount = activeFilterCount(filters);

  const filterPanelProps = {
    filters,
    opt,
    update,
    changeTimezone,
    clearFilters,
    timezoneChoices,
    deviceTimezone,
    liveProblem,
    optionsError: options.error,
    retryOptions: loadOptions,
    activeCount,
  };

  return (
    <div className="mx-auto w-full max-w-[1400px] px-0 py-2 sm:py-4" data-testid="booking-layout">
      <div className="mb-5 flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <nav aria-label="Breadcrumb" className="text-xs font-semibold text-muted">
            <Link to="/patient/appointments" className="text-primary hover:underline">Appointments</Link>
            <span aria-hidden="true"> / </span>
            <span>Book</span>
          </nav>
          <h1 className="mt-1 text-2xl font-semibold">Book an appointment</h1>
          <p className="mt-1 text-sm text-muted">Search live availability by service, doctor, clinic, speciality, type and location.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to="/patient/ai-assistant" className="rounded-2xl border border-violet-200 bg-violet-50 px-4 py-3 text-center text-sm font-semibold text-violet-700 hover:bg-violet-100" data-testid="booking-ask-avia">
            Ask AVIA
          </Link>
          <Link to="/patient/ai-assistant" state={{ voice: true }} aria-label="Ask AVIA by voice" className="rounded-2xl border border-violet-200 bg-white px-3 py-3 text-center text-sm font-semibold text-violet-700 hover:bg-violet-50" data-testid="booking-voice-entry">
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <rect x="9" y="3" width="6" height="11" rx="3" stroke="currentColor" strokeWidth="2" />
              <path d="M5 11a7 7 0 0 0 14 0M12 18v3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </Link>
          <Link to="/patient/appointments" className="rounded-2xl border border-border bg-white px-4 py-3 text-center text-sm font-semibold hover:bg-slate-50">
            My appointments
          </Link>
        </div>
      </div>

      <div className="lg:flex lg:items-start lg:gap-6">
        <aside aria-label="Search filters" className="hidden w-80 shrink-0 lg:sticky lg:top-24 lg:block" data-testid="filter-panel">
          <div className="max-h-[calc(100vh-7rem)] overflow-y-auto rounded-3xl border border-border bg-white p-5 shadow-soft">
            <DiscoveryFilters idPrefix="search" {...filterPanelProps} />
          </div>
        </aside>

        <section className="min-w-0 flex-1" aria-label="Services">
          <div className="flex items-center justify-between gap-3 lg:hidden">
            <button
              type="button"
              ref={drawerOpenerRef}
              onClick={() => setDrawerOpen(true)}
              aria-haspopup="dialog"
              aria-expanded={drawerOpen}
              className="inline-flex items-center gap-2 rounded-2xl border border-border bg-white px-4 py-2.5 text-sm font-semibold shadow-sm"
              data-testid="open-filters"
            >
              <Icon name="search" className="h-4 w-4" />
              Filters
              {activeCount ? <span className="rounded-full bg-primary px-2 py-0.5 text-xs text-white" aria-label={`${activeCount} active`}>{activeCount}</span> : null}
            </button>
          </div>

          <div className="mt-3 flex flex-col justify-between gap-2 sm:flex-row sm:items-center lg:mt-0">
            <p className="text-sm text-muted" aria-live="polite" data-testid="results-count">
              {results.loading && !results.data
                ? "Searching…"
                : results.data
                  ? total
                    ? `Showing ${firstShown}–${lastShown} of ${total} service${total === 1 ? "" : "s"}`
                    : "0 services found"
                  : ""}
              {results.loading && results.data ? " · Updating…" : ""}
            </p>
            <TimezoneNotice timeZone={results.data?.display_timezone || displayTimezone} className="text-sm" />
          </div>

          {results.error ? (
            <div className="mt-4">
              <InlineError message={readApiError(results.error, "Services could not be loaded.")} onRetry={() => setRetryTick((n) => n + 1)} />
            </div>
          ) : null}

          {results.loading && !results.data ? (
            <div className="mt-4 grid grid-cols-1 gap-5 md:grid-cols-2 2xl:grid-cols-3" data-testid="results-loading">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="h-96 animate-pulse rounded-3xl border border-border bg-white" />
              ))}
            </div>
          ) : results.data && items.length === 0 && !(total > 0) ? (
            <div className="mt-4 rounded-3xl border border-border bg-white p-10 text-center shadow-soft" data-testid="results-empty">
              <div className="text-lg font-semibold">No services match</div>
              <p className="mt-2 text-sm text-muted">Try other dates, a different type, or fewer filters.</p>
              <button type="button" onClick={clearFilters} className="mt-4 rounded-2xl border border-border bg-white px-5 py-2.5 text-sm font-semibold hover:bg-slate-50">
                Clear filters
              </button>
            </div>
          ) : items.length ? (
            <div className={classNames("mt-4 grid grid-cols-1 gap-5 md:grid-cols-2 2xl:grid-cols-3", results.loading && "opacity-70")} aria-busy={results.loading || undefined}>
              {items.map((item) => (
                <ServiceCard key={item.row_id} item={item} displayTimezone={displayTimezone} onChoose={setSelectedService} />
              ))}
            </div>
          ) : null}

          {totalPages > 1 ? (
            <Pagination page={currentPage} totalPages={totalPages} disabled={results.loading} onChange={goToPage} />
          ) : null}
        </section>
      </div>

      {drawerOpen ? (
        <FilterDrawer onClose={closeDrawer} activeCount={activeCount} total={results.data ? total : null}>
          <DiscoveryFilters idPrefix="drawer" {...filterPanelProps} />
        </FilterDrawer>
      ) : null}

      {selectedService ? (
        <ServiceBookingPanel
          key={`${selectedService.service_version_id}:${displayTimezone}`}
          service={selectedService}
          displayTimezone={displayTimezone}
          dateFrom={settled.date_from}
          dateTo={settled.date_to}
          onClose={() => setSelectedService(null)}
          onDateOfBirthSaved={() => {
            setResyncAfter(results.data);
            setRetryTick((tick) => tick + 1);
          }}
          onBooked={(appointment) => navigate(`/patient/appointments/${appointment.id}`, { state: { appointmentBooked: true } })}
        />
      ) : null}
    </div>
  );
}
